import { Button } from '@talentmesh/ui/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@talentmesh/ui/components/tooltip';
import { cn } from '@talentmesh/ui/lib/utils';

import { LoaderCircle, Save } from 'lucide-react';
import { type MRT_Row, type MRT_RowData, type MRT_TableInstance } from '../../types';
import { type BoxProps } from '../../types/mrt-ui-props';
import { MRT_Box } from '../mrt/MRT_Box';
import classes from './MRT_EditActionButtons.module.css';

interface Props<TData extends MRT_RowData> extends BoxProps {
    row: MRT_Row<TData>;
    table: MRT_TableInstance<TData>;
    variant?: 'icon' | 'text';
}

export const MRT_EditActionButtons = <TData extends MRT_RowData>({
    row,
    table,
    variant = 'icon',
    ...rest
}: Props<TData>) => {
    const {
        getState,
        options: {
            icons: { IconCircleX },
            localization,
            onCreatingRowCancel,
            onCreatingRowSave,
            onEditingRowCancel,
            onEditingRowSave,
        },
        refs: { editInputRefs },
        setCreatingRow,
        setEditingRow,
    } = table;
    const { creatingRow, editingRow, isSaving } = getState();

    const isCreating = creatingRow?.id === row.id;
    const isEditing = editingRow?.id === row.id;

    const handleCancel = () => {
        if (isCreating) {
            onCreatingRowCancel?.({ row, table });
            setCreatingRow(null);
        } else if (isEditing) {
            onEditingRowCancel?.({ row, table });
            setEditingRow(null);
        }
        row._valuesCache = {} as any; //reset values cache
    };

    const handleSubmitRow = () => {
        //look for auto-filled input values
        Object.values(editInputRefs?.current)
            .filter((inputRef) => row.id === inputRef?.name?.split('_')?.[0])
            ?.forEach((input) => {
                if (input.value !== undefined && Object.hasOwn(row?._valuesCache as object, input.name)) {
                    // @ts-ignore
                    row._valuesCache[input.name] = input.value;
                }
            });
        if (isCreating)
            onCreatingRowSave?.({
                exitCreatingMode: () => setCreatingRow(null),
                row,
                table,
                values: row._valuesCache,
            });
        else if (isEditing) {
            onEditingRowSave?.({
                exitEditingMode: () => setEditingRow(null),
                row,
                table,
                values: row?._valuesCache,
            });
        }
    };

    return (
        <MRT_Box
            {...rest}
            className={cn('mrt-edit-action-buttons', classes.root, rest?.className)}
            onClick={(e) => e.stopPropagation()}
        >
            {variant === 'icon' ? (
                <>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                aria-label={localization.cancel}
                                className="text-destructive h-9 w-9"
                                onClick={handleCancel}
                                size="icon-2xs"
                                variant="ghost"
                            >
                                <IconCircleX className="size-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{localization.cancel}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                aria-label={localization.save}
                                className="h-9 w-9"
                                disabled={isSaving}
                                onClick={handleSubmitRow}
                                size="icon-2xs"
                                variant="ghost"
                            >
                                {isSaving ? (
                                    <LoaderCircle className="size-4 animate-spin" />
                                ) : (
                                    <Save className="size-4" />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{localization.save}</TooltipContent>
                    </Tooltip>
                </>
            ) : (
                <>
                    <Button onClick={handleCancel} variant="ghost">
                        {localization.cancel}
                    </Button>
                    <Button disabled={isSaving} onClick={handleSubmitRow}>
                        {localization.save}
                    </Button>
                </>
            )}
        </MRT_Box>
    );
};
